import React from "react"
import styled from "styled-components"
import chroma from "chroma-js"
import Typography from "@mui/material/Typography"
import useId from "react-use-uuid"
import { useTheme } from "@mui/material/styles"

const ChartWrap = styled.div`
  width: ${p => p.width}px;
  height: ${p => p.width}px;
  position: relative;
  display: grid;
  grid-template-rows: 40px 1fr 60px;
  margin: 0 auto;
  padding-top: 0.5rem;
`

const TitleRow = styled.div`
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;

  > h5 {
  }
`
const ValueRow = styled.div`
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
`

const VizRow = styled.div`
  padding: 0;
  text-align: center;
  position: relative;
  overflow: hidden;
`

const BaseRect = styled.rect`
  fill: none;
  stroke: ${p => p.stroke};
  vector-effect: non-scaling-stroke;
`

const ValueRect = styled.rect`
  fill: ${p => p.fill};
  stroke: none;
`

const MaskRect = styled.rect`
  fill: black;
  fill-opacity: 0.5;
  stroke: none;
`

const TargetLine = styled.line`
  stroke: ${p => p.stroke};
  stroke-dasharray: 2, 3;
  stroke-width: 1px;
  vector-effect: non-scaling-stroke;
`

const DistanceText = styled.div`
  color: ${p => p.color};
  font-size: 1rem;
  position: absolute;
  text-align: right;
  bottom: -20px;
  height: 30px;
  transition: opacity 1s 0.4s;
  z-index: 3;
`

const DistanceLine = styled.path`
  stroke: ${p => p.stroke || "none"};
  stroke-width: 2px;
  fill: none;
  vector-effect: non-scaling-stroke;
`

const DistanceBetween = styled.path`
  stroke: none;
  fill: ${p => p.fill || "none"};
  fill-opacity: 0.8;
`

const Svg = styled.svg`
  width: 100%;
  height: 100%;
`

function defaultDistanceMessageFormatter(distance) {
  if (!distance) {
    return "Exactly on target"
  }
  return distance > 0
    ? `(${distance}% above target)`
    : `(${Math.abs(distance)}% below target)`
}

function TargetBar({
  width = 200,
  height = 200,
  target = 80,
  value = 0,
  max = 100,
  title = "Title",
  suffix = "",
  zoom = 0,
  distanceMessageFormatter = defaultDistanceMessageFormatter,
  inverted = false
}) {
  const id = useId()
  const theme = useTheme()
  const colorScales = theme.colorScales

  const viewBoxWidth = 100
  const viewBoxHeight = 100
  const svgPadding = 2
  const fractFull = Math.min(100, value / max)
  const targetFractFull = Math.min(100, target / max)
  const barHeight = viewBoxWidth - svgPadding * 2
  const targetPosY = svgPadding + (1 - target / max) * barHeight
  const maskStartY = svgPadding + barHeight * (1 - targetFractFull)
  const valueStartY = svgPadding + barHeight * (1 - fractFull)

  const isExceedingTarget = target < value

  const valueFill =
    isExceedingTarget && !inverted
      ? colorScales.warningInverted(1).css()
      : colorScales.warningInverted(0).css()

  const textureFill = chroma(valueFill)
    .darken(isExceedingTarget ? 0 : 5)
    .css()

  const maxZoomScale = Math.max(0, 0.8 - Math.abs(fractFull - targetFractFull))

  const scaledZoom = maxZoomScale * zoom
  const vbX = viewBoxWidth * 0.5 * scaledZoom
  const vbY =
    viewBoxHeight * (1 - Math.max(fractFull, targetFractFull)) * scaledZoom
  const zoomFract = 1 - scaledZoom

  const distanceBetweenPath = [
    "M",
    viewBoxWidth * 0.4,
    targetPosY,
    "V",
    valueStartY,
    "h",
    viewBoxWidth * 0.2,
    "V",
    targetPosY,
    "Z"
  ].join(" ")

  const distanceLinePath = [
    "M",
    viewBoxWidth * 0.65,
    targetPosY,
    "h 5",
    "V",
    valueStartY,
    "h -5"
  ].join(" ")

  const distanceMessage = distanceMessageFormatter(value - target)
  return (
    <ChartWrap width={width} height={height}>
      <ValueRow
        style={{ transform: `scale(${1 - zoom * 0.1}) translateY(-15px)` }}
      >
        <Typography variant="h5">
          {value}
          {suffix}
        </Typography>
        <DistanceText
          color={target === value ? theme.basePalette.fg : valueFill}
          style={{ opacity: zoom > 0 ? 1 : 0 }}
        >
          {distanceMessage}
        </DistanceText>
      </ValueRow>
      <VizRow>
        <Svg
          viewBox={`${vbX} ${vbY} ${viewBoxWidth * zoomFract} ${
            viewBoxHeight * zoomFract
          }`}
        >
          <ValueRect
            width={viewBoxWidth * 0.2}
            x={viewBoxWidth * 0.4}
            y={valueStartY}
            height={barHeight * fractFull}
            fill={valueFill}
          />
          {isExceedingTarget && (
            <MaskRect
              width={viewBoxWidth * 0.2}
              x={viewBoxWidth * 0.4}
              y={maskStartY}
              height={barHeight * targetFractFull}
            />
          )}
          <BaseRect
            width={viewBoxWidth * 0.2}
            x={viewBoxWidth * 0.4}
            y={svgPadding}
            height={barHeight}
            stroke={theme.basePalette.fg}
          />
          <TargetLine
            x1={viewBoxWidth * 0.1}
            x2={viewBoxWidth * 0.9}
            y1={targetPosY}
            y2={targetPosY}
            stroke={theme.basePalette.fg}
          />
          {zoom > 0 && target !== value && (
            <>
              <DistanceBetween
                d={distanceBetweenPath}
                fill={`url(#distance-fill-${id})`}
              />
              <DistanceLine d={distanceLinePath} stroke={valueFill} />
            </>
          )}
          <defs>
            {theme.viz.SVG.patterns.createStripePattern({
              fill: textureFill,
              id: `distance-fill-${id}`,
              textureOpacity: 0.9,
              fillOpacity: 0.1
            })}
          </defs>
        </Svg>
      </VizRow>
      <TitleRow>
        <Typography variant="h5">{title}</Typography>
      </TitleRow>
    </ChartWrap>
  )
}

export default TargetBar
